import React, { useEffect, useState } from 'react';
import {
  IProcessManagerAutomation,
  processManagerAutomationEmailToExternalSchema,
  processManagerAutomationEmailToLegalClientSchema,
  processManagerAutomationLegalRequirementChangeSchema,
  processManagerAutomationSchema,
} from '../../Interfaces/IProcessManagerAutomation';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
} from '@mui/material';
import { Add, Warning } from '@mui/icons-material';
import { useGetProcessManagerAutomationTypes } from '../../hooks/reactQuerys/ProcessManagerQueries';
import { IProcessManager } from '../../Interfaces/IProcessManager';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { EditPMAutomationRequirement } from './EditPMAutomationRequirement';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import { EditPMAutomationEmail } from './EditPMAutomationEmail';
import { ILegalClient } from '../../Interfaces/ILegalClient';

interface IProps {
  processManager: IProcessManager;
  automations: IProcessManagerAutomation[];
  setAutomations: Function;
  //
  legalClients: ILegalClient[];
  legalPhases: ILegalPhaseFullObject[];
  legalReqStates: ILegalRequirementState[];
  //
  setIsExpantedAutomations?: Function;
  /**
   * Beim Anlegen ``data.Trigger`` auf diesen Wert vorbelegen. Wird vom
   * HBCI-Tracker genutzt, um Automationen entweder dem
   * „balance-change"- oder dem „target-reached"-Bucket zuzuordnen.
   */
  defaultTrigger?: string;
}

export const AddPMAutomation: React.FC<IProps> = ({
  processManager,
  automations,
  setAutomations,
  legalClients,
  legalPhases,
  legalReqStates,
  setIsExpantedAutomations,
  defaultTrigger,
}) => {
  const getDefaultReq = () =>
    processManagerAutomationLegalRequirementChangeSchema.parse({
      idLegalRequirement: -1,
      idLegalRequirementState: 15,
    });

  const { isLoadingManagerAutomationTypes, processManagerAutomationTypes } =
    useGetProcessManagerAutomationTypes();
  //
  const [isOpen, setIsOpen] = useState(false);
  const [editAutomation, setEditAutomation] = useState(
    processManagerAutomationSchema.parse({
      idProcessManager: processManager.idProcessManager,
      data: getDefaultReq(),
    })
  );
  //
  const [isValidatedChild, setIsValidatedChild] = useState(false);

  const isValidated = isValidatedChild;

  useEffect(() => {
    console.log('editAutomation');
    console.log(editAutomation);
  }, [editAutomation]);

  const handleOpen = () => {
    const object = processManagerAutomationSchema.parse({
      idProcessManager: processManager.idProcessManager,
      data: getDefaultReq(),
    });
    object.data = getDefaultReq();

    setEditAutomation(object);
    setIsOpen(true);
  };

  const handleAdd = () => {
    let tmpId = -1;
    const saveObject = { ...editAutomation };

    if (automations.length > 0) {
      const tmpIdArray =
        Math.min(...automations.map(auto => auto.idProcessManagerAutomation)) -
        1;
      if (tmpIdArray <= tmpId) {
        tmpId = tmpIdArray;
      }
    }

    saveObject.idProcessManagerAutomation = tmpId;

    // Trigger-Tag in data einbetten — der PMHbciAccount-Driver (und
    // ggf. weitere Driver, die Multi-Trigger unterstuetzen) lesen das
    // beim Sweep aus.
    if (defaultTrigger) {
      saveObject.data = {
        ...(saveObject.data as object),
        Trigger: defaultTrigger,
      } as any;
    }

    setAutomations([...automations, saveObject]);
    setIsOpen(false);

    if (setIsExpantedAutomations) {
      setIsExpantedAutomations(true);
    }
  };

  if (!isOpen || isLoadingManagerAutomationTypes) {
    return (
      <IconButton
        size='small'
        title='Automatisierung hinzufügen'
        disabled={isLoadingManagerAutomationTypes}
        onClick={handleOpen}
      >
        <Add />
      </IconButton>
    );
  }
  if (!processManagerAutomationTypes) {
    return (
      <IconButton>
        <IconButton
          size='small'
          title='Es ist ein Fehler aufgetretten!'
          disabled
        >
          <Warning />
        </IconButton>
      </IconButton>
    );
  }

  return (
    <>
      <IconButton size='small'>
        <Add />
      </IconButton>

      <Dialog open onClose={() => setIsOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle>Automatisierung hinzufügen</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item sm={12} sx={{ mt: 2 }}>
              <CustomeTextField
                attr='idProcessManagerAutomationType'
                label='Automatisierung'
                object={editAutomation}
                setObject={(updadedAutomation: IProcessManagerAutomation) => {
                  if (updadedAutomation.idProcessManagerAutomationType === 1) {
                    updadedAutomation.data = getDefaultReq();
                  } else if (
                    updadedAutomation.idProcessManagerAutomationType === 2
                  ) {
                    updadedAutomation.data =
                      processManagerAutomationEmailToLegalClientSchema.parse({
                        idLegalClient: -1,
                      });
                  } else if (
                    updadedAutomation.idProcessManagerAutomationType === 3
                  ) {
                    updadedAutomation.data =
                      processManagerAutomationEmailToExternalSchema.parse({
                        Receiver: '',
                      });
                  } else {
                    updadedAutomation.data = {};
                  }
                  setEditAutomation(updadedAutomation);
                }}
                type='select'
              >
                {processManagerAutomationTypes.map(autoType => (
                  <MenuItem
                    key={`select-add-autotype-${autoType.idProcessManagerAutomationType}`}
                    value={autoType.idProcessManagerAutomationType}
                  >
                    {autoType.ProcessManagerAutomationType}
                  </MenuItem>
                ))}
              </CustomeTextField>
            </Grid>

            {editAutomation.idProcessManagerAutomationType === 1 &&
              processManagerAutomationLegalRequirementChangeSchema.safeParse(
                editAutomation.data
              ).success && (
                <EditPMAutomationRequirement
                  processManager={processManager}
                  editAutomation={editAutomation}
                  setEditAutomation={setEditAutomation}
                  legalPhases={legalPhases}
                  legalReqStates={legalReqStates}
                  setIsValidatedChild={setIsValidatedChild}
                />
              )}
            {[2, 3].includes(editAutomation.idProcessManagerAutomationType) && (
              <EditPMAutomationEmail
                processManager={processManager}
                editAutomation={editAutomation}
                setEditAutomation={setEditAutomation}
                legalClients={legalClients}
                setIsValidatedChild={setIsValidatedChild}
              />
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setIsOpen(false)}>
            Abbruch
          </Button>
          <Button
            variant='contained'
            disabled={!isValidated}
            onClick={handleAdd}
          >
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
